import { useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute, redirect } from '@tanstack/react-router'
import { plansQueryOptions } from '@/lib/queries/admin'
import { billingAccount } from '@/lib/queries/billing-accounts'
import { creditUsage } from '@/lib/queries/credit-usage'
import { organization } from '@/lib/queries/organizations'
import { BillingPage } from '@/features/organizations/billing/billing-page'
import type { CheckoutReturn } from '@/features/organizations/billing/use-checkout-return'

/**
 * The billing ACCOUNT, seen from inside an org. Every org on the account sees
 * the same page, because the figures are shared. Org admins only — the same
 * gate as Settings.
 */
export const Route = createFileRoute('/_authenticated/org/$orgId/billing/')({
  validateSearch: (search: Record<string, unknown>): CheckoutReturn => {
    const out: CheckoutReturn = {}
    if (search.checkout === 'success' || search.checkout === 'cancel') {
      out.checkout = search.checkout
    }
    if (search.kind === 'plan' || search.kind === 'pack') {
      out.kind = search.kind
    }
    if (typeof search.session_id === 'string' && search.session_id) {
      out.session_id = search.session_id
    }
    return out
  },
  beforeLoad: ({ context, params }) => {
    if (!context.isOrgAdmin) {
      throw redirect({ to: '/org/$orgId', params })
    }
  },
  loader: async ({ context, params }) => {
    const org = await context.queryClient.query({
      ...organization.findOne(params.orgId),
      staleTime: 'static',
    })
    await Promise.all([
      context.queryClient.query({
        ...billingAccount.findOne(org.billing_account_id),
        staleTime: 'static',
      }),
      context.queryClient.query({
        ...creditUsage.entitlements(org.billing_account_id),
        staleTime: 'static',
      }),
      context.queryClient.query({
        ...creditUsage.balance(org.billing_account_id),
        staleTime: 'static',
      }),
      context.queryClient.query({
        ...plansQueryOptions(),
        staleTime: 'static',
      }),
    ])
  },
  component: OrgBillingPage,
})

function OrgBillingPage() {
  const { orgId } = Route.useParams()
  const search = Route.useSearch()
  const { data: org } = useSuspenseQuery(organization.findOne(orgId))
  const { data: account } = useSuspenseQuery(
    billingAccount.findOne(org.billing_account_id)
  )
  const { data: entitlements } = useSuspenseQuery(
    creditUsage.entitlements(org.billing_account_id)
  )

  return (
    <BillingPage
      orgId={orgId}
      billingAccountId={org.billing_account_id}
      accountName={account.name}
      entitlements={entitlements}
      search={search}
    />
  )
}
